/** @typedef {import('./types').CrmApiError} CrmApiError */

const GENERIC_MESSAGE = 'Something went wrong. Please try again.'
const NETWORK_MESSAGE = 'Cannot reach the server. Check your connection and try again.'
const CRM_NOT_ALLOWED_MESSAGE = "You don't have access to CRM. Ask your administrator to turn it on for your account."
const ADMIN_REQUIRED_MESSAGE = 'Only an administrator can do this.'

/**
 * Normalise anything thrown by the CRM client into { code, message, status }.
 * The backend sends { ErrorCode, Message }. For 400/403/409 the Message is shown
 * to the user; for 404 a short fixed text; for 500 and network failures a generic one.
 * @param {unknown} err
 * @returns {CrmApiError}
 */
export const toApiError = err => {
    if (!err || typeof err !== 'object') {
        return { code: 'UNKNOWN', message: GENERIC_MESSAGE, status: 0 }
    }
    const e = /** @type {{status?: number, data?: any, response?: any, message?: string, name?: string, isAxiosError?: boolean}} */ (err)
    // A failure that never reached the network layer (e.g. a TypeError in our own code) is not "cannot reach
    // the server": only request errors (CrmRequestError, axios errors, objects carrying a status / response) are.
    const isRequestError = e.name === 'CrmRequestError' || e.isAxiosError === true || 'status' in e || 'response' in e
    if (!isRequestError) {
        return { code: 'UNKNOWN', message: GENERIC_MESSAGE, status: 0 }
    }
    const status = Number(e.status ?? e.response?.status ?? 0) || 0
    const data = e.data ?? e.response?.data
    const code =
        data && typeof data === 'object' && typeof data.ErrorCode === 'string'
            ? data.ErrorCode
            : ''
    const serverMessage =
        data && typeof data === 'object' && typeof data.Message === 'string'
            ? data.Message.trim()
            : ''

    if (status === 0) {
        return { code: 'NETWORK', message: NETWORK_MESSAGE, status: 0 }
    }
    // The two 403s every CRM user can meet: show the server's Message, with fixed wording if it sends none.
    if (status === 403 && code === 'CRM_NOT_ALLOWED') {
        return { code, message: serverMessage || CRM_NOT_ALLOWED_MESSAGE, status }
    }
    if (status === 403 && code === 'ADMIN_REQUIRED') {
        return { code, message: serverMessage || ADMIN_REQUIRED_MESSAGE, status }
    }
    if ([400, 403, 409].includes(status)) {
        return {
            code: code || 'REQUEST_REJECTED',
            message: serverMessage || GENERIC_MESSAGE,
            status,
        }
    }
    if (status === 404) {
        return { code: code || 'NOT_FOUND', message: 'That record was not found.', status }
    }
    if (status === 401) {
        return { code: code || 'UNAUTHORIZED', message: 'Your session has expired. Please sign in again.', status }
    }
    return { code: code || 'SERVER_ERROR', message: GENERIC_MESSAGE, status }
}

/** True when the error is a 403 ADMIN_REQUIRED. */
export const isAdminRequired = apiError => apiError?.code === 'ADMIN_REQUIRED'

/** True when the signed-in user has no CRM access (403 CRM_NOT_ALLOWED). */
export const isCrmNotAllowed = apiError => apiError?.code === 'CRM_NOT_ALLOWED'
